import { F } from '@/utils/intl';
import React from 'react';
import { useIntl } from 'umi';
import ProForm, { ProFormText } from '@ant-design/pro-form';
import { Col, Row, Space, Spin } from 'antd';
import InfoCard from '@/components/ProjectDetails/Cards/InfoCard';
import LeadLocale from '@/components/Lead/LeadLocale';
import { UserOutlined } from '@ant-design/icons';
import LeadSalutation from '@/components/Lead/LeadSalutation';
import { FormInstance } from 'antd/lib/form';
import styles from './index.less';
import ExtraFields from '@/components/LeadSource/ManualLead/ExtraFields';

interface CustomerFormProps {
  form: FormInstance;
  isCustomerFormDisabled: boolean;
  isSearchingCustomers: boolean;
  customerRequiredFields: string[];
  onEmailBlur: (e: React.FocusEvent<HTMLInputElement>) => void;
  onPhoneBlur: (e: React.FocusEvent<HTMLInputElement>) => void;
  onCustomerExtraFieldValuesChange: (values: any) => void;
}

const CustomerForm: React.FC<CustomerFormProps> = ({
  form,
  isCustomerFormDisabled,
  isSearchingCustomers,
  customerRequiredFields,
  onEmailBlur,
  onPhoneBlur,
  onCustomerExtraFieldValuesChange,
}) => {
  const intl = useIntl();

  // Helper function to check if a field is required
  const isFieldRequired = (fieldName: string) => {
    return !isCustomerFormDisabled && customerRequiredFields.includes(fieldName);
  };

  return (
    <div>
      <InfoCard
        style={{
          display: !isCustomerFormDisabled ? 'block' : 'none',
        }}
        title={
          <Space>
            <UserOutlined />
            <F
              id={'pages.lead.customer_information'}
              defaultMessage={'Customer Information'}
            />
          </Space>
        }
        variant="blue"
        className={styles.infoCardSection}
      >
        <ProForm
          form={form}
          autoComplete="off"
          size="small"
          submitter={false}
          variant={'underlined'}
        >
          <Row gutter={16}>
            <Col span={4}>
              <ProForm.Item
                label={
                  <F id={'customer.salutation'} defaultMessage={'Salutation'} />
                }
                rules={[
                  {
                    required: isFieldRequired('gender'),
                  },
                ]}
                name="gender"
              >
                <LeadSalutation
                  lead={null}
                  selectProps={{
                    variant: 'underlined',
                    size: 'small',
                    allowClear: true,
                    disabled: isCustomerFormDisabled,
                  }}
                />
              </ProForm.Item>
            </Col>
            <Col span={10}>
              <ProFormText hidden name={'customerId'} />
              <ProFormText
                label={intl.formatMessage({
                  id: 'pages.customers.firstName',
                  defaultMessage: 'First Name',
                })}
                name="firstname"
                disabled={isCustomerFormDisabled}
                rules={[
                  {
                    required: !isCustomerFormDisabled,
                  },
                ]}
                fieldProps={{
                  autoComplete: 'nope',
                  'data-testid': 'customer-firstname',
                }}
              />
            </Col>
            <Col span={10}>
              <ProFormText
                label={intl.formatMessage({
                  id: 'pages.customers.lastName',
                  defaultMessage: 'Last Name',
                })}
                name={'lastname'}
                rules={[
                  {
                    required: !isCustomerFormDisabled,
                  },
                ]}
                fieldProps={{
                  autoComplete: 'nope',
                  'data-testid': 'customer-lastname',
                }}
              />
            </Col>
          </Row>

          <Spin spinning={isSearchingCustomers}>
            <Row gutter={16}>
              <Col span={10}>
                <ProFormText
                  label={intl.formatMessage({
                    id: 'common.attribute.email',
                    defaultMessage: 'Email',
                  })}
                  name="email"
                  rules={[
                    {
                      required: isFieldRequired('email'),
                    },
                    {
                      type: 'email',
                    },
                  ]}
                  fieldProps={{
                    autoComplete: 'nope',
                    onBlur: onEmailBlur,
                    'data-testid': 'customer-email',
                  }}
                />
              </Col>
              <Col span={10}>
                <ProFormText
                  label={intl.formatMessage({
                    id: 'common.attribute.phone',
                    defaultMessage: 'Phone',
                  })}
                  name="phone"
                  rules={[
                    {
                      required: isFieldRequired('phone'),
                    },
                  ]}
                  fieldProps={{
                    autoComplete: 'nope',
                    onBlur: onPhoneBlur,
                  }}
                />
              </Col>
              <Col span={4}>
                <ProForm.Item
                  label={
                    <F
                      id={'pages.leads.filters.locale.placeholder'}
                      defaultMessage={'Language'}
                    />
                  }
                  rules={[
                    {
                      required: isFieldRequired('locale'),
                    },
                  ]}
                  name="locale"
                >
                  <LeadLocale
                    selectProps={{
                      variant: 'underlined',
                      size: 'small',
                      disabled: isCustomerFormDisabled,
                    }}
                  />
                </ProForm.Item>
              </Col>
            </Row>
          </Spin>

          {/* Customer Extra Fields */}
          <ExtraFields
            relatedTo="customer"
            onValuesChange={onCustomerExtraFieldValuesChange}
          />
        </ProForm>
      </InfoCard>
    </div>
  );
};

export default CustomerForm;
